// eslint-disable-next-line no-restricted-imports
import { type SeverityLevel } from '@sentry/types';

import {
  Actionability,
  CapabilityTier,
  CustomerImpact,
  EventClassification,
} from '@canvas/operational-types';

import rankings from './generate-rankings-tool/__generated__/rankings.json';
import { type RankingDataTuple, rankingDataToKey } from './ranking-key';

export const getRank = (data: RankingDataTuple): number => {
  return rankings[rankingDataToKey(data) as keyof typeof rankings];
};

/**
 * Specifies at what rank should we use which severity
 */
export const rankingThresholdsToSeverity: [number, SeverityLevel][] = [
  /**
   * Any of these, with enough volume, are very likely to result in a HOT
   */
  [
    getRank([
      EventClassification.BUG,
      CustomerImpact.CUSTOMERS,
      CapabilityTier.C3,
      Actionability.UNKNOWN,
    ]),
    // Color: Deep red
    'fatal',
  ],
  /**
   * Any of these, with enough volume, could result in a HOT IF it turns out they're bugs, impact customers
   * and the error is related t a tiered capability
   */
  [
    getRank([
      EventClassification.UNKNOWN,
      CustomerImpact.UNKNOWN,
      CapabilityTier.NOT_SPECIFIED,
      Actionability.UNKNOWN,
    ]),
    // Color: RED
    'error',
  ],
  /**
   * This is the lowest rank of potential bugs where we probably still care to investigate.
   * i.e. We have a possible bug that affects our team - That's still something we care about, just lower priority,
   */
  [
    getRank([
      EventClassification.UNKNOWN,
      CustomerImpact.INTERNAL_TEAM,
      CapabilityTier.NOT_SPECIFIED,
      Actionability.ACTIONABLE,
    ]),
    // Color: Yellow
    'warning',
  ],
  /**
   * A bug that impacts no one for a something that isn't a tiered capability? Probably something you could technically ignore forever if you really wanted.
   */
  [
    getRank([
      EventClassification.UNKNOWN,
      CustomerImpact.NONE,
      CapabilityTier.NONE,
      Actionability.ACTIONABLE,
    ]),
    // Color: Yellow. Could choose "info" but the color is blue which would suggest no bug at all
    'warning',
  ],
  /**
   * Normal logs inherently don't have to be looked into so we give them the lowest severity (which happens to be called 'log')
   */
  [
    getRank([
      EventClassification.LOG,
      CustomerImpact.CUSTOMERS,
      CapabilityTier.C1,
      Actionability.ACTIONABLE,
    ]),
    // Color: Blue
    'log',
  ],
];
